import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Link from 'src/components/ui/Link';
import HorizontalScroller, { SnapItem } from 'src/components/ui/HorizontalScroller';
import Typography from 'src/components/ui/Typography';
import { GalleryPreviewCard } from 'src/components/favorites/GalleryPreview';

import { cardHeightInRemForGalleries } from 'src/constants/misc';
import theme from 'src/utils/theme';

import { GalleryAccessPolicy, type CompanyProfileQuery } from 'src/types/__generated__/graphql';
import { routePathCompany } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';
import { useGalleries } from 'src/hooks/useGalleries';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const StyledLink = styledComponent(Link)`
    color: ${theme.palette.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
`;

export interface Props {
    companyProfile: NonNullable<CompanyProfileQuery['companyProfile']>;
}

const defaultLimit = 5;
const cardsWidthInRem = 18;

const CompanyGalleriesSection: React.FC<Props> = ({ companyProfile }) => {
    const { t } = useTranslation(['profile']);

    const {
        items: galleries,
        totalCount,
        loading,
        fetchMore,
    } = useGalleries({
        companyId: companyProfile.id,
        policy: GalleryAccessPolicy.Public,
        limit: defaultLimit,
        fetchPolicy: 'cache-and-network',
    });

    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Box my={2}>
                    <Typography variant="h2" serif>
                        {t('profile:galleries')}
                    </Typography>
                </Box>
                <HorizontalScroller
                    actualCardsLength={galleries?.length}
                    totalCardsLength={totalCount}
                    loading={loading}
                    cardHeightInRem={cardHeightInRemForGalleries}
                    cardWidthInPx={cardsWidthInRem * 16}
                    marginBottom={2}
                    handleFetchMore={fetchMore}
                >
                    {galleries?.map((gallery) => (
                        <SnapItem key={gallery.id}>
                            <Box width={`${cardsWidthInRem}rem`}>
                                <GalleryPreviewCard gallery={gallery} />
                            </Box>
                        </SnapItem>
                    ))}
                </HorizontalScroller>
                {galleries && galleries.length > 0 ? (
                    <Box textAlign="right">
                        <StyledLink href={`${routePathCompany}/${companyProfile.id}/galleries`}>
                            {t('profile:seeAllGalleries')}
                        </StyledLink>
                    </Box>
                ) : null}
            </StyledCardContent>
        </Card>
    );
};

export default CompanyGalleriesSection;
